import { useEffect, useState } from "react";
import api, { formatError, API } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileText, ExternalLink, ShieldOff, Loader2, RefreshCw } from "lucide-react";
import TrialBadge from "@/components/TrialBadge";

/**
 * SuperAdmin KYC Review Queue — surfaces every hospital/clinic waiting on
 * verification along with their CMHO license, submitted document, and clear
 * Approve / Reject controls. Rejection is gated behind a reason dialog so
 * every rejection carries an audit trail (delivered via WhatsApp + SMS).
 */
export default function SuperAdminKycQueue() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/kyc?status=${status}`);
      setRows(data.kyc || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const openDoc = (r) => {
    if (!r.kyc_document_url) return;
    // Fetch as blob using auth (superadmin cookie/header) then open in a tab.
    api.get(r.kyc_document_url.replace("/api", ""), { responseType: "blob" })
      .then(({ data, headers }) => {
        const blob = new Blob([data], { type: headers["content-type"] || r.kyc_document_content_type });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((err) => toast.error(formatError(err.response?.data?.detail) || "Could not open document"));
  };

  const approve = async (r) => {
    if (!window.confirm(`Approve KYC for "${r.org_name}" and make it live?`)) return;
    setBusyId(r.id);
    try {
      await api.post(`/superadmin/kyc/${r.id}/approve`);
      toast.success(`${r.org_name} approved and live`);
      await load(tab);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (reason.length < 3) {
      toast.error("Reason must be at least 3 characters");
      return;
    }
    setBusyId(rejectTarget.id);
    try {
      await api.post(`/superadmin/kyc/${rejectTarget.id}/reject`, { reason });
      toast.success(`${rejectTarget.org_name} rejected`);
      setRejectTarget(null);
      setRejectReason("");
      await load(tab);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const kycBadge = (s) => {
    if (s === "approved") return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20">Approved</Badge>;
    if (s === "rejected") return <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/30 hover:bg-rose-500/20">Rejected</Badge>;
    return <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/20">Pending</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="sa-kyc-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-heading text-lg font-semibold text-white">KYC Review Queue</h3>
          <p className="text-xs text-slate-400">Verify CMHO license &amp; registration documents. Approvals notify admins via WhatsApp + SMS.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => load(tab)} className="bg-slate-800/50 border-slate-700 text-slate-200 hover:bg-slate-800 gap-1.5" data-testid="sa-kyc-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="pending" data-testid="sa-kyc-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="sa-kyc-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="sa-kyc-tab-rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-slate-800">
                  <TableHead className="text-slate-300">Organization</TableHead>
                  <TableHead className="text-slate-300">Type</TableHead>
                  <TableHead className="text-slate-300">License #</TableHead>
                  <TableHead className="text-slate-300">Document</TableHead>
                  <TableHead className="text-slate-300">Submitted</TableHead>
                  <TableHead className="text-slate-300">KYC</TableHead>
                  <TableHead className="text-slate-300 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow className="border-slate-700"><TableCell colSpan={7} className="text-center text-slate-400 py-10"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow className="border-slate-700"><TableCell colSpan={7} className="text-center text-slate-400 py-10" data-testid="sa-kyc-empty">No {tab} KYC records.</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} className="border-slate-700 hover:bg-slate-700/40" data-testid={`kyc-row-${r.id}`}>
                    <TableCell>
                      <div className="font-medium text-white">{r.org_name}</div>
                      <div className="text-xs text-slate-400">{r.email} · {r.name}</div>
                      {r.trial?.active && <div className="mt-1"><TrialBadge trial={r.trial} className="text-[10px] py-0.5 px-2" /></div>}
                      {r.kyc_reject_reason && (
                        <div className="text-[11px] text-rose-300 mt-1 max-w-xs">Reason: {r.kyc_reject_reason}</div>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="border-slate-600 text-slate-200">{r.org_type}</Badge></TableCell>
                    <TableCell className="font-mono text-slate-200">{r.license_number || "—"}</TableCell>
                    <TableCell>
                      {r.kyc_document_url ? (
                        <Button size="sm" variant="outline" onClick={() => openDoc(r)} className="bg-slate-700/40 border-slate-600 text-sky-300 hover:bg-slate-700 gap-1.5" data-testid={`kyc-doc-view-${r.id}`}>
                          <FileText className="w-3.5 h-3.5" /> Open
                          <ExternalLink className="w-3 h-3 ml-0.5" />
                        </Button>
                      ) : <span className="text-slate-500 text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-slate-400 text-xs">{r.kyc_submitted_at ? new Date(r.kyc_submitted_at).toLocaleString() : "—"}</TableCell>
                    <TableCell>{kycBadge(r.kyc_status)}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      {r.kyc_status !== "approved" && (
                        <Button size="sm" disabled={busyId === r.id} className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => approve(r)} data-testid={`kyc-approve-${r.id}`}>
                          {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
                        </Button>
                      )}
                      {r.kyc_status !== "rejected" && (
                        <Button size="sm" variant="outline" className="border-rose-500 text-rose-300 hover:bg-rose-500/10"
                          onClick={() => { setRejectTarget(r); setRejectReason(""); }}
                          data-testid={`kyc-reject-${r.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="kyc-reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShieldOff className="w-4 h-4 text-rose-400" /> Reject {rejectTarget?.org_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-300">Enter a reason. This will be sent to the admin via WhatsApp + SMS.</p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Registration document illegible. Please re-upload a clearer scan."
              className="bg-slate-800 border-slate-700 text-slate-100 min-h-24"
              data-testid="kyc-reject-reason-input"
              maxLength={280}
            />
            <div className="text-[11px] text-slate-500 text-right">{rejectReason.length}/280</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="kyc-reject-cancel">Cancel</Button>
            <Button onClick={submitReject} disabled={busyId === rejectTarget?.id || rejectReason.trim().length < 3} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="kyc-reject-confirm">
              {busyId === rejectTarget?.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" /> Confirm Reject</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
